import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Grid,
  Stack,
  Box,
  Paper,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Checkbox,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { debounce } from "lodash";
import moment from "moment";
import { ContainerCard, NormalTextField, HeaderButton } from "../../components";
import NoDataCard from "../../components/cards/NoDataCard";
import LoaderCard from "../../components/cards/LoaderCard";
import { api, urls } from "../../services";
import { useAuth, useToast } from "../../contexts";

// Frozen push API contract (all actions share one endpoint). Logged to the
// console on push API errors to make production debugging easier.
const PUSH_API_CONTRACT = `POST api/magazine/v1/admin/send_push
  preview : {title, message, push_type, audience}
  confirm : POST api/magazine/v1/admin/send_push {action: "confirm", job_id, expected_count}
  cancel  : POST api/magazine/v1/admin/send_push {action: "cancel", job_id}`;

// Test-send destination is typed by the sender per use (no hardcoded personal
// number: the test send is a REAL push and must never fire at a fixed phone)

const PHONE_CODES = ["91", "966", "965", "971", "973", "974"];

const PUSH_TYPES = [
  { value: "subscription", label: "Subscription" },
  { value: "magazine", label: "Magazine" },
  { value: "article", label: "Article" },
];

const PLATFORM_OPTIONS = [
  { value: "all", label: "Both" },
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];

const SUB_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active only" },
  { value: "lapsed", label: "Lapsed only" },
];

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 12;

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    "&.MuiTypography-root": {
      fontSize: 24,
      fontWeight: 600,
    },
  },
  sectionTitle: {
    "&.MuiTypography-root": {
      fontSize: 20,
      fontWeight: 600,
    },
  },
  lookupLine: {
    "&.MuiTypography-root": {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  bigCount: {
    "&.MuiTypography-root": {
      fontSize: 56,
      fontWeight: 700,
      lineHeight: 1.1,
    },
  },
  stickyBar: {
    position: "sticky",
    bottom: 8,
    zIndex: 2,
    padding: theme.spacing(2),
    borderRadius: 8,
  },
}));

const formatDate = (value) => {
  if (!value) {
    return "--";
  }
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY hh:mm A") : String(value);
};

const statusChipColor = (status) => {
  if (status === "done") {
    return "success";
  }
  if (status === "failed") {
    return "error";
  }
  if (status === "processing" || status === "queued" || status === "pending") {
    return "info";
  }
  return "default";
};

export function SendPush() {
  const classes = useStyles();
  const theme = useTheme();
  const { logout } = useAuth();
  const { showToast } = useToast();

  // stage machine: compose -> preview -> result
  const [stage, setStage] = useState("compose");
  const [busy, setBusy] = useState(false);

  // compose form
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [pushType, setPushType] = useState("subscription");
  const [audienceMode, setAudienceMode] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [subStatus, setSubStatus] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [phoneCode, setPhoneCode] = useState("91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [lookup, setLookup] = useState({
    state: "idle",
    line: "Enter the phone number to verify the recipient.",
    sub: "",
  });

  // preview step
  const [preview, setPreview] = useState({
    jobId: "",
    count: 0,
    dryRun: null,
  });
  const [test, setTest] = useState({ state: "idle", note: "" });
  // test-send destination, typed by the sender (default empty — never hardcoded)
  const [testPhone, setTestPhone] = useState({ code: "91", number: "" });

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [sending, setSending] = useState(false);

  // result step
  const [result, setResult] = useState({ row: null, stalled: false });

  // history
  const [historyOpen, setHistoryOpen] = useState(true);
  const [history, setHistory] = useState({
    rows: [],
    count: 0,
    loading: false,
  });

  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const contentValid =
    title.trim().length > 0 &&
    title.trim().length <= 180 &&
    message.trim().length > 0 &&
    message.trim().length <= 300;

  const phoneResolved =
    audienceMode === "all" ||
    (phoneNumber.trim().length >= 6 && lookup.state === "found");

  const canCheckReach = contentValid && phoneResolved && !busy;

  const buildAudience = () => {
    if (audienceMode === "phone") {
      return {
        platform: "all",
        staleness: "all",
        sub_status: "all",
        phones: [
          { code: phoneCode, number: phoneNumber.trim() },
        ],
      };
    }
    return {
      platform: platform,
      staleness: includeInactive ? "all" : "fresh_90",
      sub_status: subStatus,
    };
  };

  const audienceSummary = () => {
    if (audienceMode === "phone") {
      return `Audience: specific phone (+${phoneCode} ${phoneNumber.trim()})`;
    }
    const platformLabel =
      (PLATFORM_OPTIONS.find((o) => o.value === platform) ?? {}).label ??
      platform;
    const subLabel =
      (SUB_STATUS_OPTIONS.find((o) => o.value === subStatus) ?? {}).label ??
      subStatus;
    return `Audience: all opted-in subscribers | Platform: ${platformLabel} | Subscription: ${subLabel} | Long-inactive: ${
      includeInactive ? "included" : "excluded"
    }`;
  };

  const logPushApiContract = () => {
    console.info("[SendPush] push API contract:\n" + PUSH_API_CONTRACT);
  };

  const handlePushApiError = (err, onDone) => {
    if (err.response?.status === 401) {
      logout();
      if (onDone) {
        onDone();
      }
      return;
    }
    logPushApiContract();
    showToast(err.response?.data?.message ?? "Something went wrong", "error");
    if (onDone) {
      onDone();
    }
  };

  const fetchHistory = () => {
    return api
      .get(urls.pushHistory, { params: { page: 1, limit: 20 } })
      .then((res) => res.data?.data ?? {});
  };

  // poll push_history until the job row reaches a terminal status
  const startPolling = (jobId, handlers) => {
    stopPolling();
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      fetchHistory()
        .then((data) => {
          const rows = data.results ?? [];
          const row = rows.find((r) => r.uuid === jobId) ?? null;
          const status = row?.status ?? "";
          if (row && (status === "done" || status === "failed")) {
            stopPolling();
            if (handlers?.onRow) {
              handlers.onRow(row);
            }
            if (handlers?.onDone) {
              handlers.onDone(row);
            }
          } else {
            if (handlers?.onRow) {
              handlers.onRow(row);
            }
            if (attempts >= MAX_POLLS) {
              stopPolling();
              if (handlers?.onTimeout) {
                handlers.onTimeout(row);
              }
            } else {
              pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            }
          }
        })
        .catch(() => {
          if (attempts >= MAX_POLLS) {
            stopPolling();
            if (handlers?.onTimeout) {
              handlers.onTimeout(null);
            }
          } else {
            pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          }
        });
    };
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  // COMPOSE: debounced (600ms) live lookup of the recipient phone
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runLookup = useCallback(
    debounce((code, number) => {
      setLookup({ state: "loading", line: "Looking up...", sub: "" });
      const attempt = (pc) =>
        api
          .get(urls.userStatus, {
            params: { phone_code: pc, phone_number: number },
          })
          .then((res) => {
            const d = res.data?.data ?? {};
            if (d.exists === false) {
              return false;
            }
            return d;
          });
      attempt(code)
        .then((first) => {
          if (first === false) {
            // retry once with a leading plus (storage convention may differ)
            return attempt("+" + code);
          }
          return first;
        })
        .then((data) => {
          if (data === false) {
            setLookup({
              state: "missing",
              line: "No account for this number",
              sub: "",
            });
            return;
          }
          const name =
            data?.name ??
            data?.first_name ??
            data?.user_name ??
            data?.user?.first_name ??
            data?.user?.name ??
            null;
          const start =
            data?.subscription_start_date ?? data?.start_date ?? null;
          const end =
            data?.subscription_end_date ??
            data?.end_date ??
            data?.plan_end_date ??
            null;
          let sub = "";
          if (start && end) {
            sub = `Subscription: ${formatDate(start)} - ${formatDate(end)}`;
          } else if (end) {
            sub = `Plan valid till ${formatDate(end)}`;
          } else if (data?.is_subscribed === false) {
            sub = "No active subscription";
          }
          if (name) {
            setLookup({ state: "found", line: name, sub: sub });
          } else {
            setLookup({ state: "found", line: "New user - no account", sub: sub });
          }
        })
        .catch(() => {
          setLookup({
            state: "missing",
            line: "Lookup failed - check the number",
            sub: "",
          });
        });
    }, 600),
    []
  );

  useEffect(() => {
    return () => {
      runLookup.cancel();
    };
  }, [runLookup]);

  const handlePhoneCodeChange = (e) => {
    const code = e.target.value;
    setPhoneCode(code);
    setLookup({
      state: "idle",
      line: "Enter the phone number to verify the recipient.",
      sub: "",
    });
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setPhoneNumber(value);
    setLookup({
      state: "idle",
      line: "Enter the phone number to verify the recipient.",
      sub: "",
    });
    if (value.trim().length >= 6) {
      runLookup(phoneCode, value.trim());
    } else {
      runLookup.cancel();
    }
  };

  // COMPOSE -> PREVIEW (keepTest preserves the test-send status on re-preview)
  const runPreview = (keepTest) => {
    setBusy(true);
    api
      .post(
        urls.sendPush,
        {
          title: title.trim(),
          message: message.trim(),
          push_type: pushType,
          audience: buildAudience(),
        },
        { params: { validate: true } }
      )
      .then((res) => {
        const d = res.data?.data ?? {};
        setPreview({
          jobId: d.job_id ?? "",
          count: d.audience_count ?? 0,
          dryRun: d.dry_run ?? null,
        });
        if (!keepTest) {
          setTest({ state: "idle", note: "" });
        }
        setStage("preview");
        setBusy(false);
      })
      .catch((err) => {
        handlePushApiError(err, () => setBusy(false));
      });
  };

  // PREVIEW -> back to compose (cancel the pending preview job)
  const backToCompose = () => {
    if (preview.jobId) {
      api
        .post(urls.sendPush, { action: "cancel", job_id: preview.jobId })
        .catch(() => {});
    }
    setStage("compose");
  };

  // PREVIEW: test send to a number the sender types (preview + confirm + poll)
  const runTestSend = () => {
    const digits = (testPhone.number || "").replace(/\D/g, "");
    if (digits.length < 6) {
      setTest({
        state: "failed",
        note: "Enter the number the test copy should go to first.",
      });
      return;
    }
    setBusy(true);
    setTest({ state: "running", note: "" });
    api
      .post(
        urls.sendPush,
        {
          title: title.trim(),
          message: message.trim(),
          push_type: pushType,
          audience: {
            platform: "all",
            staleness: "all",
            sub_status: "all",
            phones: [{ code: testPhone.code, number: testPhone.number }],
          },
        },
        { params: { validate: true } }
      )
      .then((res) => {
        const d = res.data?.data ?? {};
        if (!d.job_id || (d.audience_count ?? 0) === 0) {
          setBusy(false);
          setTest({
            state: "empty",
            note: "No registered device for the test number - check it and retry",
          });
          return undefined;
        }
        return api
          .post(urls.sendPush, {
            action: "confirm",
            job_id: d.job_id,
            expected_count: d.audience_count,
          })
          .then(() => {
            startPolling(d.job_id, {
              onRow: () => {},
              onDone: (row) => {
                setBusy(false);
                setTest({
                  state: "done",
                  note: `Test push done (success ${row.success_count ?? 0} / failed ${
                    row.failure_count ?? 0
                  })`,
                });
              },
              onTimeout: () => {
                setBusy(false);
                setTest({
                  state: "done",
                  note: "Test push still sending - check history",
                });
              },
            });
          })
          .catch((err) => {
            setBusy(false);
            setTest({
              state: "failed",
              note: err.response?.data?.message ?? "Test push failed",
            });
            if (err.response?.status === 401) {
              logout();
            }
          });
      })
      .catch((err) => {
        setBusy(false);
        setTest({
          state: "failed",
          note: err.response?.data?.message ?? "Test push failed",
        });
        if (err.response?.status !== 401) {
          logPushApiContract();
        } else {
          logout();
        }
      });
  };

  const skipTest = () => {
    setTest({ state: "skipped", note: "Test send skipped." });
  };

  const testTerminal =
    test.state === "done" ||
    test.state === "failed" ||
    test.state === "empty" ||
    test.state === "skipped";

  // PREVIEW -> CONFIRM dialog
  const openConfirm = () => {
    setConfirmText("");
    setReviewed(false);
    setConfirmOpen(true);
  };

  // CONFIRM -> RESULT (poll history for the confirmed job)
  const runConfirm = () => {
    setSending(true);
    api
      .post(urls.sendPush, {
        action: "confirm",
        job_id: preview.jobId,
        expected_count: preview.count,
      })
      .then(() => {
        setSending(false);
        setConfirmOpen(false);
        setResult({ row: null, stalled: false });
        setStage("result");
        startPolling(preview.jobId, {
          onRow: (row) => setResult({ row: row, stalled: false }),
          onDone: (row) => setResult({ row: row, stalled: false }),
          onTimeout: (row) => setResult({ row: row, stalled: true }),
        });
      })
      .catch((err) => {
        setSending(false);
        setConfirmOpen(false);
        if (err.response?.status === 401) {
          logout();
          return;
        }
        logPushApiContract();
        showToast(
          err.response?.data?.message ?? "Confirmation failed",
          "error"
        );
        // count changed or preview expired: force a fresh preview
        runPreview(true);
      });
  };

  // RESULT actions
  const sendAnother = () => {
    stopPolling();
    setPreview({ jobId: "", count: 0, dryRun: null });
    setResult({ row: null, stalled: false });
    setTest({ state: "idle", note: "" });
    setAudienceMode("all");
    setPlatform("all");
    setSubStatus("all");
    setIncludeInactive(false);
    setStage("compose");
  };

  const doneReset = () => {
    sendAnother();
    setTitle("");
    setMessage("");
    setPushType("subscription");
    setPhoneNumber("");
    setPhoneCode("91");
    setTestPhone({ code: "91", number: "" });
    setLookup({
      state: "idle",
      line: "Enter the phone number to verify the recipient.",
      sub: "",
    });
  };

  // HISTORY
  const loadHistory = () => {
    setHistory((h) => ({ ...h, loading: true }));
    fetchHistory()
      .then((data) => {
        setHistory({
          rows: data.results ?? [],
          count: data.count ?? 0,
          loading: false,
        });
      })
      .catch((err) => {
        setHistory((h) => ({ ...h, loading: false }));
        if (err.response?.status === 401) {
          logout();
        } else {
          showToast(err.response?.data?.message ?? "Something went wrong", "error");
        }
      });
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderResultIcon = () => {
    const row = result.row;
    const status = row?.status ?? "";
    if (!row || (status !== "done" && status !== "failed")) {
      return (
        <ScheduleIcon sx={{ fontSize: 56, color: theme.palette.theme.main }} />
      );
    }
    if (status === "failed") {
      return (
        <CancelIcon sx={{ fontSize: 56, color: theme.palette.common.redTxt }} />
      );
    }
    if ((row.failure_count ?? 0) > 0) {
      return (
        <WarningAmberIcon
          sx={{ fontSize: 56, color: theme.palette.common.orangeTxt }}
        />
      );
    }
    return (
      <CheckCircleIcon
        sx={{ fontSize: 56, color: theme.palette.common.greenTxt }}
      />
    );
  };

  const renderResultHeadline = () => {
    const row = result.row;
    const status = row?.status ?? "";
    if (!row || (status !== "done" && status !== "failed")) {
      return "Sending in progress";
    }
    if (status === "failed") {
      return "Push failed";
    }
    if ((row.failure_count ?? 0) > 0 || (row.invalid_token_count ?? 0) > 0) {
      return "Push completed with failures";
    }
    return "Push delivered";
  };

  const renderTestLine = () => {
    if (test.state === "idle") {
      return "Optional: send a test copy first. A test send is a REAL push delivered to the number you enter below.";
    }
    if (test.state === "running") {
      return "Test push: sending...";
    }
    if (test.state === "empty" || test.state === "failed") {
      return test.note;
    }
    if (test.state === "skipped") {
      return test.note;
    }
    return test.note;
  };

  const renderTestLineColor = () => {
    if (test.state === "empty" || test.state === "failed") {
      return theme.palette.common.redTxt;
    }
    if (test.state === "done") {
      return theme.palette.common.greenTxt;
    }
    return theme.palette.common.lightGreyTxt;
  };

  const renderCompose = () => {
    return (
      <ContainerCard>
        <Stack spacing={3}>
          <Typography className={classes.sectionTitle}>
            Audience
          </Typography>
          <RadioGroup
            row
            value={audienceMode}
            onChange={(e) => setAudienceMode(e.target.value)}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All opted-in subscribers"
            />
            <FormControlLabel
              value="phone"
              control={<Radio />}
              label="Specific phone number"
            />
          </RadioGroup>
          {audienceMode === "all" && (
            <Fragment>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                  Platform
                </Typography>
                <RadioGroup
                  row
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                >
                  {PLATFORM_OPTIONS.map((o) => (
                    <FormControlLabel
                      key={o.value}
                      value={o.value}
                      control={<Radio />}
                      label={o.label}
                    />
                  ))}
                </RadioGroup>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  select
                  label="Subscription status"
                  value={subStatus}
                  onChange={(e) => setSubStatus(e.target.value)}
                  sx={{ width: 300 }}
                >
                  {SUB_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                }
                label="Include long-inactive devices"
              />
            </Fragment>
          )}
          {audienceMode === "phone" && (
            <Stack spacing={1}>
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  label="Code"
                  value={phoneCode}
                  onChange={handlePhoneCodeChange}
                  sx={{ width: 140 }}
                >
                  {PHONE_CODES.map((c) => (
                    <MenuItem key={c} value={c}>
                      +{c}
                    </MenuItem>
                  ))}
                </TextField>
                <NormalTextField
                  label="Phone number"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  type="number"
                  charLimit={15}
                />
              </Stack>
              <Box>
                <Typography
                  className={classes.lookupLine}
                  sx={{
                    color:
                      lookup.state === "found"
                        ? theme.palette.common.greenTxt
                        : lookup.state === "missing"
                        ? theme.palette.common.redTxt
                        : theme.palette.common.lightGreyTxt,
                  }}
                >
                  {lookup.line}
                </Typography>
                {lookup.sub && (
                  <Typography
                    className={classes.lookupLine}
                    sx={{ color: theme.palette.common.lightGreyTxt }}
                  >
                    {lookup.sub}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
          <Divider />
          <Typography className={classes.sectionTitle}>Content</Typography>
          <NormalTextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            charLimit={180}
            helperText={`${title.length}/180`}
          />
          <NormalTextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={4}
            charLimit={300}
            helperText={`${message.length}/300`}
          />
          <Accordion
            disableGutters
            elevation={0}
            sx={{ bgcolor: "transparent" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                Advanced
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                select
                label="Notification type"
                value={pushType}
                onChange={(e) => setPushType(e.target.value)}
                sx={{ width: 300 }}
              >
                {PUSH_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </ContainerCard>
    );
  };

  const renderPreview = () => {
    const dryRun = preview.dryRun;
    return (
      <ContainerCard>
        <Stack spacing={2}>
          <Typography className={classes.sectionTitle}>
            Audience reach
          </Typography>
          <Box>
            <Typography className={classes.bigCount}>
              {preview.count}
            </Typography>
            <Typography sx={{ fontSize: 16 }}>
              devices will receive this push
            </Typography>
            {audienceMode === "all" && includeInactive && (
              <Typography sx={{ fontSize: 14, color: theme.palette.common.lightGreyTxt }}>
                Including long-inactive devices
              </Typography>
            )}
            {dryRun && (
              <Typography sx={{ fontSize: 14, color: theme.palette.common.lightGreyTxt }}>
                {`Numbers checked: ${dryRun.checked ?? 0} - valid: ${
                  dryRun.valid ?? 0
                } - invalid: ${dryRun.invalid ?? 0}`}
              </Typography>
            )}
            {audienceMode === "phone" && (
              <Typography sx={{ fontSize: 14, color: theme.palette.common.lightGreyTxt }}>
                {`Recipient: +${phoneCode} ${phoneNumber.trim()}`}
              </Typography>
            )}
          </Box>
          {preview.count === 0 && (
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: theme.palette.common.redTxt }}
            >
              No devices match - widen your filters
            </Typography>
          )}
          <Divider />
          <Box>
            <Typography
              sx={{ fontSize: 14, fontWeight: 500, color: renderTestLineColor() }}
            >
              {renderTestLine()}
            </Typography>
            {!testTerminal && preview.count > 0 && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <TextField
                  select
                  label="Code"
                  value={testPhone.code}
                  onChange={(e) =>
                    setTestPhone((p) => ({ ...p, code: e.target.value }))
                  }
                  sx={{ width: 110 }}
                  size="small"
                >
                  {PHONE_CODES.map((c) => (
                    <MenuItem key={c} value={c}>
                      +{c}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Test number (gets a REAL push)"
                  value={testPhone.number}
                  onChange={(e) =>
                    setTestPhone((p) => ({
                      ...p,
                      number: e.target.value.replace(/\D/g, "").slice(0, 12),
                    }))
                  }
                  type="number"
                  size="small"
                  sx={{ width: 260 }}
                />
              </Stack>
            )}
            {!testTerminal && preview.count > 0 && (
              <Button
                size="small"
                sx={{ textTransform: "none", mt: 0.5 }}
                onClick={skipTest}
                disabled={busy}
              >
                Skip test
              </Button>
            )}
          </Box>
        </Stack>
      </ContainerCard>
    );
  };

  const renderResult = () => {
    const row = result.row;
    const status = row?.status ?? "processing";
    const success = row?.success_count ?? 0;
    const failure = row?.failure_count ?? 0;
    const invalid = row?.invalid_token_count ?? 0;
    return (
      <ContainerCard>
        <Stack spacing={2} alignItems="flex-start">
          <Stack direction="row" spacing={2} alignItems="center">
            {renderResultIcon()}
            <Box>
              <Typography className={classes.sectionTitle}>
                {renderResultHeadline()}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  size="small"
                  label={result.stalled ? "in progress" : status}
                  color={statusChipColor(result.stalled ? "processing" : status)}
                />
                <Typography sx={{ fontSize: 14 }}>
                  {`Finished: ${formatDate(row?.finished_at)}`}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.palette.common.greenTxt }}>
                {success}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>Delivered</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.palette.common.redTxt }}>
                {failure}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>Failed</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: theme.palette.common.orangeTxt }}>
                {invalid}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>Invalid tokens</Typography>
            </Box>
          </Stack>
          {result.stalled && (
            <Typography sx={{ fontSize: 14, color: theme.palette.common.lightGreyTxt }}>
              Still sending - check Recent manual pushes below.
            </Typography>
          )}
        </Stack>
      </ContainerCard>
    );
  };

  const renderHistory = () => {
    return (
      <Accordion
        expanded={historyOpen}
        onChange={(e, expanded) => setHistoryOpen(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Typography className={classes.sectionTitle}>
              {`Recent manual pushes${history.count > 0 ? ` (${history.count})` : ""}`}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  loadHistory();
                }}
                disabled={busy || history.loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {history.loading && <LoaderCard height="200px" />}
          {!history.loading && (
            <Fragment>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Created
                      </TableCell>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Title
                      </TableCell>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Audience
                      </TableCell>
                      <TableCell sx={{ fontSize: 16, fontWeight: 600 }}>
                        Success / Failure
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.rows.map((r) => (
                      <TableRow key={r.uuid ?? r.created_at} hover>
                        <TableCell>{formatDate(r.created_at)}</TableCell>
                        <TableCell>{r.title}</TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {r.push_type}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.status}
                            color={statusChipColor(r.status)}
                          />
                        </TableCell>
                        <TableCell>{r.audience_count ?? 0}</TableCell>
                        <TableCell>
                          {`${r.success_count ?? 0} / ${r.failure_count ?? 0}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {history.rows.length === 0 && (
                <NoDataCard title="No manual pushes yet" height="150px" />
              )}
            </Fragment>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBarButtons = () => {
    if (stage === "compose") {
      return (
        <Stack direction="row" spacing={2} sx={{ width: 320 }}>
          <HeaderButton
            type="primary"
            onClick={runPreview}
            disabled={!canCheckReach}
          >
            {busy ? "Checking..." : "Check reach"}
          </HeaderButton>
        </Stack>
      );
    }
    if (stage === "preview") {
      return (
        <Stack direction="row" spacing={2} sx={{ width: 640 }}>
          <HeaderButton
            type="secondary"
            onClick={backToCompose}
            disabled={busy}
          >
            Back
          </HeaderButton>
          <HeaderButton
            type="secondary"
            onClick={runTestSend}
            disabled={
              busy ||
              testTerminal ||
              !(preview.count > 0 || audienceMode === "phone") ||
              (testPhone.number || "").replace(/\D/g, "").length < 6
            }
          >
            Send test copy
          </HeaderButton>
          <HeaderButton
            type="primary"
            onClick={openConfirm}
            disabled={busy || preview.count === 0 || !testTerminal}
          >
            Continue
          </HeaderButton>
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={2} sx={{ width: 420 }}>
        <HeaderButton type="secondary" onClick={sendAnother} disabled={busy}>
          Send another
        </HeaderButton>
        <HeaderButton type="primary" onClick={doneReset} disabled={busy}>
          Done
        </HeaderButton>
      </Stack>
    );
  };

  return (
    <Fragment>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Typography className={classes.pageTitle}>Send Push</Typography>
        </Grid>
        <Grid item>
          {stage === "compose" && renderCompose()}
          {stage === "preview" && renderPreview()}
          {stage === "result" && renderResult()}
        </Grid>
        <Grid item>{renderHistory()}</Grid>
      </Grid>
      <Paper className={classes.stickyBar} elevation={1} sx={{ mt: 2 }}>
        {renderBarButtons()}
      </Paper>
      <Dialog
        open={confirmOpen}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { p: 2 }, elevation: 0 }}
      >
        <DialogTitle sx={{ fontSize: 24, fontWeight: 700, textAlign: "center" }}>
          Confirm push
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>{`Title: ${
              title.length > 40 ? `${title.slice(0, 40)}...` : title
            }`}</Typography>
            <Typography>{`Message: ${message}`}</Typography>
            <Typography sx={{ fontSize: 14, color: theme.palette.common.lightGreyTxt }}>
              {audienceSummary()}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
              {`This will deliver to ~${preview.count} devices.`}
            </Typography>
            {preview.count > 100 ? (
              <TextField
                label="Type SEND to confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={sending}
              />
            ) : (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reviewed}
                    onChange={(e) => setReviewed(e.target.checked)}
                    disabled={sending}
                  />
                }
                label="I reviewed the recipient details"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, justifyContent: "center", gap: 2 }}>
          <Stack direction="row" spacing={2} sx={{ width: 420 }}>
            <HeaderButton
              type="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
            >
              Go back
            </HeaderButton>
            <HeaderButton
              type="primary"
              onClick={runConfirm}
              disabled={
                sending ||
                (preview.count > 100 && confirmText !== "SEND") ||
                (preview.count <= 100 && !reviewed)
              }
            >
              {sending ? "Sending..." : "Send now"}
            </HeaderButton>
          </Stack>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
